import { defineStore } from "pinia"
import { HttpService } from "@/helpers/http-service"
import { CONSTANT } from "@/helpers/constant"
import type { JcConfigFullResponse, JcConfigGroup, JcConfigProduct, JcFilterOption } from "@/types/jcFilterSystem"

const httpService = new HttpService()

interface JcConfigFullState {
  configuratorId: string | null
  products: JcConfigProduct[]
  groups: Record<string, JcConfigGroup>
  groupOrder: string[]
  manufacturers: JcFilterOption[]
  loading: boolean
  loadedAt: number | null
}

export const useJcConfigFullStore = defineStore({
  id: "jc.config.full",
  state: (): JcConfigFullState => ({
    configuratorId: null,
    products: [],
    groups: {},
    groupOrder: [],
    manufacturers: [],
    loading: false,
    loadedAt: null,
  }),
  getters: {
    filterableGroups(state): JcConfigGroup[] {
      return state.groupOrder.map((gid) => state.groups[gid]).filter((g): g is JcConfigGroup => !!g && g.filterable)
    },
    displayGroups(state): JcConfigGroup[] {
      return state.groupOrder.map((gid) => state.groups[gid]).filter((g): g is JcConfigGroup => !!g && !g.filterable)
    },
    groupForOption(state) {
      return (optionId: string): JcConfigGroup | null => {
        for (const gid of state.groupOrder) {
          const group = state.groups[gid]
          if (!group) continue
          if (group.options.some((opt) => opt.id === optionId)) return group
        }
        return null
      }
    },
  },
  actions: {
    async load(configuratorId: string, force = false): Promise<boolean> {
      if (!configuratorId) return false
      if (!force && this.configuratorId === configuratorId && this.loadedAt) return true

      this.loading = true
      try {
        const url = CONSTANT.API.JC_FILTER.CONFIG_FULL.replace("{id}", configuratorId)
        const response: any = await httpService.post(url, {})
        const data: JcConfigFullResponse = response?.data ?? {
          products: [],
          groups: {},
          groupOrder: [],
          manufacturers: [],
        }
        this.products = data.products ?? []
        this.groups = data.groups ?? {}
        this.groupOrder = data.groupOrder ?? []
        this.manufacturers = data.manufacturers ?? []
        this.configuratorId = configuratorId
        this.loadedAt = Date.now()
        return true
      } catch (error) {
        console.error("Failed to load /config/{id}/full", error)
        this.products = []
        this.groups = {}
        this.groupOrder = []
        this.manufacturers = []
        return false
      } finally {
        this.loading = false
      }
    },

    matchingProducts(selection: Record<string, string>): JcConfigProduct[] {
      const entries = Object.entries(selection)
      return this.products.filter((p) => entries.every(([groupKey, optId]) => (p.options[groupKey] ?? []).includes(optId)))
    },

    availableInGroup(groupKey: string, selection: Record<string, string>): Set<string> {
      const subset = { ...selection }
      delete subset[groupKey]
      const matches = this.matchingProducts(subset)
      const avail = new Set<string>()
      matches.forEach((p) => (p.options[groupKey] ?? []).forEach((id) => avail.add(id)))
      return avail
    },

    reset() {
      this.configuratorId = null
      this.products = []
      this.groups = {}
      this.groupOrder = []
      this.manufacturers = []
      this.loadedAt = null
    },
  },
})
